/*
 * The two folders of mail that's waiting: Scheduled (sends at a time; "Cancel" puts it back in Drafts) and Snoozed
 * (comes back to Inbox, unread, at a time; "Unsnooze" brings it back now). Each says its time in this browser's time
 * zone, and why one was postponed or didn't go, in the API's words.
 */
import { useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { cx } from "../lib/cx";
import { Button } from "../ui/Button";
import { IconAlertCircle, IconClock, Spinner } from "../ui/icons";
import { useToast } from "../ui/Toast";
import { ApiError, wmApi } from "./api";
import { keys, useScheduled, useSnoozed, whenWords } from "./data";
import { plural } from "./format";
import { IconAlarm, IconSendLater } from "./icons";
import { Empty } from "./parts";

function Header({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="flex h-12 shrink-0 items-center gap-2 border-b border-line px-4">
      <h2 className="truncate text-[15px] font-semibold text-fg">{title}</h2>
      <span className="truncate text-[12px] text-dim">{subtitle}</span>
    </div>
  );
}

function Item({ active, onOpen, children, action }: { active: boolean; onOpen: (() => void) | null; children: ReactNode; action: ReactNode }) {
  return (
    <li className={cx("flex items-start gap-3 border-b border-line-soft px-4 py-3", active ? "bg-selected" : "bg-surface", onOpen && !active && "hover:bg-hover")}>
      <div
        role={onOpen ? "button" : undefined}
        tabIndex={onOpen ? 0 : undefined}
        onClick={onOpen ?? undefined}
        onKeyDown={(e) => onOpen && e.key === "Enter" && onOpen()}
        className={cx("min-w-0 flex-1", onOpen && "cursor-pointer")}
      >
        {children}
      </div>
      <div className="shrink-0">{action}</div>
    </li>
  );
}

function Skeleton() {
  return (
    <div className="space-y-px" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div key={i} className="space-y-2 px-4 py-3">
          <div className="hx-skeleton h-3 w-1/2 rounded" />
          <div className="hx-skeleton h-3 w-2/3 rounded" />
        </div>
      ))}
    </div>
  );
}

export function ScheduledList({ mb, openUid, onOpen }: { mb: string; openUid: number | null; onOpen: (folder: string, uid: number) => void }) {
  const q = useScheduled(mb);
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const list = q.data ?? [];
  const cancel = async (id: string) => {
    setBusy(id);
    try {
      await wmApi.post(`/m/${mb}/scheduled/cancel`, { id });
      toast({ tone: "success", title: "Back in Drafts. It won't be sent." });
    } catch (err) {
      toast({ tone: "error", title: err instanceof ApiError ? err.message : "It wasn't canceled. Try again." });
    } finally {
      setBusy(null);
      void qc.invalidateQueries({ queryKey: keys.scheduled(mb) });
      void qc.invalidateQueries({ queryKey: keys.folders(mb) });
      void qc.invalidateQueries({ queryKey: keys.lists(mb) });
    }
  };
  return (
    <>
      <Header title="Scheduled" subtitle={q.data ? plural(list.length, "message") + " waiting" : ""} />
      <div className="min-h-0 flex-1 overflow-y-auto">
        {q.isPending ? (
          <Skeleton />
        ) : q.isError ? (
          <p className="px-6 py-10 text-center text-[13px] text-dim">{(q.error as Error).message}</p>
        ) : !list.length ? (
          <Empty icon={<IconSendLater size={20} />} title="Nothing scheduled">
            Use the arrow beside Send to send later.
          </Empty>
        ) : (
          <ul>
            {list.map((m) => (
              <Item
                key={m.id}
                active={m.uid !== null && m.uid === openUid}
                onOpen={m.uid !== null ? () => onOpen(m.folder, m.uid!) : null}
                action={
                  m.status !== "sending" ? (
                    <Button size="sm" variant="secondary" loading={busy === m.id} loadingText="Canceling" onClick={() => void cancel(m.id)}>
                      Cancel
                    </Button>
                  ) : null
                }
              >
                <p className="truncate text-[14px] font-medium text-fg">{m.subject || "(no subject)"}</p>
                <p className="truncate text-[12.5px] text-dim">To {m.to.map((x) => (typeof x === "string" ? x : x.name || x.address)).join(", ") || "no one yet"}</p>
                {m.status === "sending" ? (
                  <p className="mt-1 flex items-center gap-1.5 text-[12.5px] text-body">
                    <Spinner size={12} /> Sending now
                  </p>
                ) : m.status === "failed" ? (
                  <p className="mt-1 flex items-start gap-1.5 text-[12.5px] text-bad">
                    <IconAlertCircle size={13} className="mt-0.5 shrink-0" />
                    <span>Didn't send{m.error ? `: ${m.error}` : "."} Open it to check, or cancel it to edit.</span>
                  </p>
                ) : m.postponedUntil ? (
                  <p className="mt-1 flex items-start gap-1.5 text-[12.5px] text-body">
                    <IconClock size={13} className="mt-0.5 shrink-0 text-dim" />
                    <span>
                      Postponed to {whenWords(m.postponedUntil)}
                      {m.error ? <span className="text-dim">. {m.error}</span> : "."}
                    </span>
                  </p>
                ) : (
                  <p className="mt-1 text-[12.5px] text-body">Sends {whenWords(m.sendAt)}</p>
                )}
              </Item>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

export function SnoozedList({ mb, openUid, onOpen }: { mb: string; openUid: number | null; onOpen: (folder: string, uid: number) => void }) {
  const q = useSnoozed(mb);
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const list = q.data ?? [];
  const unsnooze = async (id: string) => {
    setBusy(id);
    try {
      await wmApi.post(`/m/${mb}/messages/unsnooze`, { ids: [id] });
      toast({ tone: "success", title: "Back in Inbox." });
    } catch (err) {
      toast({ tone: "error", title: err instanceof ApiError ? err.message : "It wasn't brought back. Try again." });
    } finally {
      setBusy(null);
      void qc.invalidateQueries({ queryKey: keys.snoozed(mb) });
      void qc.invalidateQueries({ queryKey: keys.folders(mb) });
      void qc.invalidateQueries({ queryKey: keys.lists(mb) });
    }
  };
  return (
    <>
      <Header title="Snoozed" subtitle={q.data ? plural(list.length, "conversation") : ""} />
      <div className="min-h-0 flex-1 overflow-y-auto">
        {q.isPending ? (
          <Skeleton />
        ) : q.isError ? (
          <p className="px-6 py-10 text-center text-[13px] text-dim">{(q.error as Error).message}</p>
        ) : !list.length ? (
          <Empty icon={<IconAlarm size={20} />} title="Nothing snoozed">
            Snoozed conversations come back to Inbox, unread.
          </Empty>
        ) : (
          <ul>
            {list.map((m) => (
              <Item
                key={m.id}
                active={m.uid !== null && m.uid === openUid}
                onOpen={m.uid !== null ? () => onOpen(m.folder, m.uid!) : null}
                action={
                  <Button size="sm" variant="secondary" loading={busy === m.id} loadingText="Bringing back" onClick={() => void unsnooze(m.id)}>
                    Unsnooze
                  </Button>
                }
              >
                <p className="truncate text-[14px] font-medium text-fg">{m.subject || "(no subject)"}</p>
                {m.from ? <p className="truncate text-[12.5px] text-dim">From {typeof m.from === "string" ? m.from : m.from.name || m.from.address}</p> : null}
                {m.status === "failed" ? (
                  <p className="mt-1 flex items-start gap-1.5 text-[12.5px] text-bad">
                    <IconAlertCircle size={13} className="mt-0.5 shrink-0" />
                    <span>It didn't come back{m.error ? `: ${m.error}` : "."} Unsnooze it to try again.</span>
                  </p>
                ) : (
                  <p className="mt-1 text-[12.5px] text-body">Back {whenWords(m.until)}</p>
                )}
              </Item>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
